import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

const normalizeReleaseTarget = (target) => {
  const scope = target?.scope === 'selected_orgs'
    ? 'selected_orgs'
    : 'all_matching_orgs';

  const organizationIds = Array.isArray(target?.organization_ids)
    ? target.organization_ids.map((x) => String(x)).filter(Boolean)
    : [];

  return {
    scope,
    organizationIds,
  };
};

const getOrgDisplayName = (org) => {
  const name = org?.name || 'Unnamed org';
  const organizationId = org?.organization_id;
  return organizationId ? `${name} (${organizationId})` : name;
};

const ReleaseHeader = ({
  colors,
  noReleases,
  selectedRelease,
  readiness,
  isBusy,
  sortedReleases,
  selectedReleaseId,
  setSelectedReleaseId,
  canCancelRelease,
  canDeleteRelease,
  handleCancelRelease,
  handleDeleteRelease,
  setCreateOpen,

  allOrgs = [],
  onUpdateReleaseTarget,
  updatingReleaseTarget = false,
}) => {
  const [targetScope, setTargetScope] = useState('all_matching_orgs');
  const [selectedTargetOrgIds, setSelectedTargetOrgIds] = useState([]);

  const releaseStatus = selectedRelease?.status || 'draft';

  const targetLocked = useMemo(() => {
    if (!selectedRelease) return true;

    const lockedReleaseStatuses = ['scheduled', 'releasing', 'released', 'canceled'];
    const lockedTrackStatuses = ['scheduled', 'releasing', 'released', 'canceled'];

    if (lockedReleaseStatuses.includes(releaseStatus)) return true;

    const contactStatus = selectedRelease?.contact?.status || 'draft';
    const digitalStatus = selectedRelease?.digital?.status || 'draft';

    return (
      lockedTrackStatuses.includes(contactStatus) ||
      lockedTrackStatuses.includes(digitalStatus)
    );
  }, [selectedRelease, releaseStatus]);

  const canEditTarget = Boolean(
    selectedRelease &&
    !targetLocked &&
    !isBusy &&
    !updatingReleaseTarget
  );

  const orgOptions = useMemo(() => {
    const list = [...allOrgs];

    list.sort((a, b) => {
      const an = String(a?.name || '').toLowerCase();
      const bn = String(b?.name || '').toLowerCase();
      return an.localeCompare(bn);
    });

    return list;
  }, [allOrgs]);

  const orgByOrganizationId = useMemo(() => {
    const map = new Map();

    for (const org of orgOptions) {
      if (org?.organization_id != null) {
        map.set(String(org.organization_id), org);
      }
    }

    return map;
  }, [orgOptions]);

  const selectedTargetOrgs = useMemo(() => {
    return selectedTargetOrgIds
      .map((id) => orgByOrganizationId.get(String(id)))
      .filter(Boolean);
  }, [selectedTargetOrgIds, orgByOrganizationId]);

  const existingTarget = useMemo(
    () => normalizeReleaseTarget(selectedRelease?.target),
    [selectedRelease]
  );

  useEffect(() => {
    setTargetScope(existingTarget.scope);
    setSelectedTargetOrgIds(existingTarget.organizationIds);
  }, [existingTarget.scope, selectedRelease?._id, selectedRelease?.id]);

  const targetHasChanges = useMemo(() => {
    if (targetScope !== existingTarget.scope) return true;

    const current = [...selectedTargetOrgIds].map(String).sort();
    const existing = [...existingTarget.organizationIds].map(String).sort();

    if (current.length !== existing.length) return true;

    for (let i = 0; i < current.length; i += 1) {
      if (current[i] !== existing[i]) return true;
    }

    return false;
  }, [targetScope, selectedTargetOrgIds, existingTarget]);

  const selectedOrgCount = selectedTargetOrgIds.length;

  const handleSaveTarget = async () => {
    if (!canEditTarget || !onUpdateReleaseTarget) return;

    const selectedOrgs = selectedTargetOrgIds
      .map((id) => orgByOrganizationId.get(String(id)))
      .filter(Boolean);

    const target = {
      scope: targetScope,
      organization_ids:
        targetScope === 'selected_orgs'
          ? selectedTargetOrgIds.map((x) => Number(x)).filter((x) => Number.isFinite(x))
          : [],
      org_ids:
        targetScope === 'selected_orgs'
          ? selectedOrgs.map((org) => org?._id).filter(Boolean)
          : [],
      org_names_snapshot:
        targetScope === 'selected_orgs'
          ? selectedOrgs.map((org) => org?.name).filter(Boolean)
          : [],
    };

    await onUpdateReleaseTarget(target);
  };

  const formatTrackSchedule = (value) => {
    if (!value) {
      return {
        utc: 'UTC: —',
        local: 'Your time: —',
      };
    }

    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) {
      return {
        utc: 'UTC: Invalid date',
        local: 'Your time: Invalid date',
      };
    }

    const timeZone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local browser time';

    return {
      utc: `UTC: ${dt.toISOString()}`,
      local: `Your time (${timeZone}): ${dt.toLocaleString()}`,
    };
  };

  const renderTrackCard = (label, track) => {
    const enabled = Boolean(track?.enabled);
    const status = track?.status || 'draft';
    const schedule = formatTrackSchedule(track?.scheduled_release_date_utc);

    let borderColor = colors.primary[300];
    let backgroundColor = colors.primary[500];
    let titleColor = colors.grey[100];

    if (!enabled) {
      borderColor = colors.redAccent[700];
      backgroundColor = colors.redAccent[900];
      titleColor = colors.redAccent[200];
    } else if (status === 'scheduled') {
      borderColor = colors.greenAccent[400];
      backgroundColor = colors.greenAccent[900];
      titleColor = colors.greenAccent[300];
    } else if (status === 'releasing') {
      borderColor = colors.blueAccent[400];
      backgroundColor = colors.blueAccent[900];
      titleColor = colors.blueAccent[200];
    } else if (status === 'released') {
      borderColor = colors.greenAccent[400];
      backgroundColor = colors.greenAccent[900];
      titleColor = colors.greenAccent[300];
    }

    return (
      <Box
        sx={{
          minWidth: 240,
          flex: 1,
          p: 1.5,
          borderRadius: 1.5,
          border: `1px solid ${borderColor}`,
          backgroundColor,
        }}
      >
        <Typography variant="overline" sx={{ color: colors.grey[300], letterSpacing: 1 }}>
          {label}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            color: titleColor,
            lineHeight: 1.15,
            mb: 0.75,
          }}
        >
          {!enabled
            ? 'Not Included'
            : status === 'scheduled'
              ? 'Scheduled'
              : status === 'releasing'
                ? 'Releasing'
                : status === 'released'
                  ? 'Released'
                  : 'Not Finalized'}
        </Typography>

        {enabled ? (
          <>
            <Typography variant="caption" sx={{ color: colors.grey[100], display: 'block' }}>
              {schedule.utc}
            </Typography>

            <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block', mt: 0.25 }}>
              {schedule.local}
            </Typography>

            {track?.delivery_run_id ? (
              <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block', mt: 0.5 }}>
                Run ID: {track.delivery_run_id}
              </Typography>
            ) : null}
          </>
        ) : (
          <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block' }}>
            This sub-release is not part of the current monthly release.
          </Typography>
        )}
      </Box>
    );
  };

  const renderTargetControls = () => {
    if (noReleases || !selectedRelease) return null;

    const limitedMode = targetScope === 'selected_orgs';

    return (
      <Box
        mt={2}
        sx={{
          p: 1.5,
          borderRadius: 1.5,
          border: `1px solid ${limitedMode ? colors.greenAccent[500] : colors.primary[300]}`,
          backgroundColor: colors.primary[500],
        }}
      >
        <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100] }}>
              Release Target
            </Typography>

            <Typography variant="body2" sx={{ color: colors.grey[300], mt: 0.25 }}>
              Controls which customer orgs are eligible when this release is finalized.
            </Typography>
          </Box>

          <Chip
            size="small"
            label={
              limitedMode
                ? `Selected orgs only: ${selectedOrgCount}`
                : 'All matching orgs'
            }
            sx={{
              bgcolor: limitedMode ? colors.greenAccent[700] : colors.blueAccent[700],
              color: colors.grey[100],
              fontWeight: 700,
            }}
          />
        </Box>

        {targetLocked ? (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Release targeting is locked after a release or sub-release is scheduled, released, canceled, or actively releasing.
          </Alert>
        ) : null}

        <Box mt={1.5}>
          <FormControlLabel
            control={
              <Switch
                checked={limitedMode}
                disabled={!canEditTarget}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setTargetScope(checked ? 'selected_orgs' : 'all_matching_orgs');
                }}
                sx={{
                  width: 52,
                  height: 32,
                  padding: 0.75,

                  '& .MuiSwitch-switchBase': {
                    padding: '7px',
                    color: colors.grey[100],

                    '&.Mui-checked': {
                      transform: 'translateX(20px)',
                      color: colors.grey[900],

                      '& + .MuiSwitch-track': {
                        backgroundColor: colors.greenAccent[500],
                        opacity: 1,
                        border: `1px solid ${colors.greenAccent[300]}`,
                      },

                      '& .MuiSwitch-thumb': {
                        backgroundColor: colors.grey[100],
                        boxShadow: `0 0 0 2px ${colors.greenAccent[300]}`,
                      },
                    },

                    '&.Mui-disabled': {
                      color: colors.grey[500],

                      '& + .MuiSwitch-track': {
                        backgroundColor: colors.primary[300],
                        opacity: 0.7,
                      },
                    },

                    '&.Mui-disabled.Mui-checked': {
                      color: colors.grey[300],

                      '& + .MuiSwitch-track': {
                        backgroundColor: colors.greenAccent[800],
                        opacity: 0.8,
                        border: `1px solid ${colors.greenAccent[600]}`,
                      },

                      '& .MuiSwitch-thumb': {
                        backgroundColor: colors.greenAccent[200],
                      },
                    },
                  },

                  '& .MuiSwitch-thumb': {
                    width: 18,
                    height: 18,
                    backgroundColor: colors.grey[100],
                  },

                  '& .MuiSwitch-track': {
                    borderRadius: 999,
                    backgroundColor: colors.primary[300],
                    opacity: 1,
                    border: `1px solid ${colors.primary[200]}`,
                  },
                }}
              />
            }
            label="Limit this release to selected organizations"
          />
        </Box>

        {limitedMode ? (
          <Box mt={1.5}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={orgOptions}
              value={selectedTargetOrgs}
              disabled={!canEditTarget}
              isOptionEqualToValue={(option, value) =>
                String(option?.organization_id) === String(value?.organization_id)
              }
              getOptionLabel={(option) => getOrgDisplayName(option)}
              onChange={(event, value) => {
                setSelectedTargetOrgIds(
                  value
                    .map((org) => org?.organization_id)
                    .filter((x) => x != null)
                    .map(String)
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Selected organizations"
                  placeholder="Search orgs..."
                  helperText={
                    selectedOrgCount
                      ? `${selectedOrgCount} org${selectedOrgCount === 1 ? '' : 's'} selected`
                      : 'No orgs selected. Finalize will be blocked until at least one matching org is selected.'
                  }
                />
              )}
            />
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: colors.grey[300], mt: 1 }}>
            This release will apply to every org that has at least one product included in the Contact or Digital release track.
          </Typography>
        )}

        <Box mt={1.5} display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <Button
            variant="outlined"
            disabled={!canEditTarget || !targetHasChanges || updatingReleaseTarget}
            onClick={handleSaveTarget}
            sx={{
              borderColor: colors.greenAccent[500],
              color: colors.greenAccent[200],
              fontWeight: 800,
              '&:hover': {
                borderColor: colors.greenAccent[300],
                backgroundColor: colors.greenAccent[900],
              },
            }}
          >
            {updatingReleaseTarget ? 'Saving Target...' : 'Save Release Target'}
          </Button>

          {targetHasChanges ? (
            <Chip
              size="small"
              label="Unsaved target changes"
              sx={{
                bgcolor: colors.redAccent[700],
                color: colors.grey[100],
              }}
            />
          ) : (
            <Chip
              size="small"
              label="Target saved"
              sx={{
                bgcolor: colors.primary[300],
                color: colors.grey[100],
              }}
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderRadius: 1, mb: 2, background: colors.primary[400] }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
        <Box sx={{ flex: 1, minWidth: 320 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Release Delivery
          </Typography>

          {!noReleases && selectedRelease ? (
            <>
              <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 'bold', color: colors.greenAccent[300] }}>
                {selectedRelease.name || 'Unnamed Release'}
              </Typography>

              <Box mt={1} display="flex" gap={1} flexWrap="wrap" alignItems="center">
                <Chip size="small" label={`Status: ${selectedRelease.status || 'unknown'}`} />
                <Chip
                  size="small"
                  label={`Readiness: ${readiness.confirmed}/${readiness.total}`}
                  sx={{ bgcolor: colors.blueAccent[700], color: colors.grey[100] }}
                />
              </Box>

              <Box mt={1.5} display="flex" gap={1.5} flexWrap="wrap">
                {renderTrackCard('Contact Track', selectedRelease?.contact)}
                {renderTrackCard('Digital Track', selectedRelease?.digital)}
              </Box>
            </>
          ) : null}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {!noReleases && (
            <TextField
              select
              size="small"
              label="Release"
              value={selectedReleaseId}
              onChange={(e) => setSelectedReleaseId(e.target.value)}
              sx={{ minWidth: 260 }}
            >
              {sortedReleases.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name || '(unnamed)'} — {r.status || 'unknown'}
                </MenuItem>
              ))}
            </TextField>
          )}

          {!noReleases && selectedRelease && (
            <Button
              variant="outlined"
              disabled={isBusy || !canCancelRelease}
              onClick={handleCancelRelease}
              sx={{
                fontWeight: 700,
                borderWidth: 2,
                backgroundColor: colors.grey[100],
                color: colors.redAccent[500],
                borderColor: colors.redAccent[500],

                '&:hover': {
                  backgroundColor: colors.redAccent[500],
                  color: colors.grey[100],
                  borderColor: colors.redAccent[400],
                },

                '&.Mui-disabled': {
                  backgroundColor: colors.primary[300],
                  color: colors.grey[400],
                  borderColor: colors.primary[200],
                  opacity: 1,
                },
              }}
            >
              Cancel Release
            </Button>
          )}

          {!noReleases && selectedRelease && (
            <Button
              variant="outlined"
              disabled={isBusy || !canDeleteRelease}
              onClick={handleDeleteRelease}
              sx={{
                fontWeight: 800,
                borderWidth: 2,
                backgroundColor: colors.redAccent[100],
                color: colors.redAccent[600],
                borderColor: colors.redAccent[500],
                '&:hover': {
                  backgroundColor: colors.redAccent[500],
                  color: colors.grey[100],
                  borderColor: colors.redAccent[400],
                },
                '&.Mui-disabled': {
                  backgroundColor: colors.primary[300],
                  color: colors.grey[400],
                  borderColor: colors.primary[200],
                  opacity: 1,
                },
              }}
            >
              Delete Release
            </Button>
          )}

          <Button
            variant="contained"
            disabled={isBusy}
            onClick={() => setCreateOpen(true)}
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[100],
              '&:hover': { backgroundColor: colors.greenAccent[400] },
            }}
          >
            Create Release
          </Button>
        </Stack>
      </Box>

      {renderTargetControls()}

      {!noReleases && selectedRelease ? (
        <Box mt={2}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" sx={{ color: colors.grey[200] }}>
              Confirmation Progress
            </Typography>
            <Typography variant="caption" sx={{ color: colors.grey[200] }}>
              {readiness.pct}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={readiness.pct}
            sx={{
              height: 10,
              borderRadius: 999,
              backgroundColor: colors.primary[300],
              '& .MuiLinearProgress-bar': { backgroundColor: colors.greenAccent[500] },
            }}
          />
        </Box>
      ) : null}
    </Paper>
  );
};

export default ReleaseHeader;